import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { MdClose, MdOutlineCheckCircle } from "react-icons/md";
import {
  TbLoader2,
  TbFileInvoice,
  TbDownload,
  TbShieldCheck,
  TbCurrencyRupee,
  TbArrowRight,
  TbCheck,
  TbAlertCircle,
  TbRefresh,
  TbCreditCard,
} from "react-icons/tb";
import { FiPackage, FiTruck } from "react-icons/fi";
import toast from "react-hot-toast";
import axios from "../../../Utils/axiosInstance";
import { generateInvoicePDF, downloadBlob } from "../../../Utils/Pdf/generateInvoicePDFWeb";
import OTPModal from './../RequirementsPost/Components/OTPModal';
import { s3Upload } from "../../../Utils/s3Upload";
import { useLockBodyScroll } from "../../Hooks/useLockBodyScroll";

// ─── Constants ───────────────────────────────────────────────────────────────
const STEPS = {
  INVOICE: "invoice",       // show proforma invoice details
  DECLARATION: "declaration",
  OTP: "otp",
  BREAKDOWN: "breakdown",   // price breakdown + pay button
  PROCESSING: "processing",
  SUCCESS: "success",
  FAILED: "failed",
};

const LC_AMOUNT_THRESHOLD = 50000;

const RAZORPAY_CDN = "https://checkout.razorpay.com/v1/checkout.js";

const roundAmount = (amount) => {
  const n = Number(amount || 0);
  return n % 1 !== 0 ? Math.round(n * 100) / 100 : Math.round(n);
};

const fmt = (amount) => {
  const n = Number(amount || 0);
  if (isNaN(n)) return "0";
  return n % 1 !== 0
    ? n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : n.toLocaleString("en-IN");
};

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = RAZORPAY_CDN;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const buildAddress = (addrObj) => {
  if (!addrObj) return "N/A";
  if (typeof addrObj === "string") return addrObj;
  const { fullAddress, addressLine1, addressLine2, city, state, pincode } = addrObj;
  if (fullAddress) return fullAddress;
  return (
    [addressLine1, addressLine2, city, state, pincode].filter(Boolean).join(", ") || "N/A"
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────
const Row = ({ label, value, accent }) => (
  <div className="flex items-start justify-between gap-3 py-2.5 border-b border-gray-50 last:border-0">
    <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide flex-shrink-0 min-w-[130px] pt-0.5">
      {label}
    </span>
    <span
      className={`text-sm font-semibold text-right break-words ${accent ? "text-primaryColor font-extrabold text-base" : "text-gray-800"
        }`}
    >
      {value}
    </span>
  </div>
);

const Divider = () => <div className="h-px bg-gray-100 my-3" />;

const SkeletonRow = ({ wide = false }) => (
  <div className="flex items-start justify-between gap-3 py-2.5 border-b border-gray-50 last:border-0 animate-pulse">
    <div className="h-3 w-24 bg-gray-200 rounded-md" />

    <div
      className={`h-4 rounded-md bg-gray-200 ${wide ? "w-40" : "w-24"
        }`}
    />
  </div>
);

const StepIndicator = ({ currentStep }) => {
  const steps = [
    { id: STEPS.INVOICE, label: "Invoice" },
    { id: STEPS.DECLARATION, label: "Declaration" },
    { id: STEPS.OTP, label: "OTP" },
    { id: STEPS.BREAKDOWN, label: "Payment" },
  ];
  const activeIdx = steps.findIndex((s) => s.id === currentStep);
  return (
    <div className="flex items-center justify-center gap-1 mb-4">
      {steps.map((s, idx) => {
        const done = idx < activeIdx;
        const active = idx === activeIdx;
        return (
          <div key={s.id} className="flex items-center gap-1">
            <div
              className={`flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-black transition-all duration-300 ${done
                ? "bg-primaryColor text-white"
                : active
                  ? "bg-primaryColor/20 text-primaryColor border-2 border-primaryColor"
                  : "bg-gray-100 text-gray-400"
                }`}
            >
              {done ? <TbCheck size={11} /> : idx + 1}
            </div>
            <span
              className={`text-[9px] font-bold uppercase tracking-wide hidden sm:block ${active ? "text-primaryColor" : done ? "text-gray-500" : "text-gray-300"
                }`}
            >
              {s.label}
            </span>
            {idx < steps.length - 1 && (
              <div
                className={`w-6 h-px mx-0.5 ${done ? "bg-primaryColor" : "bg-gray-200"}`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

// ─── Payment Method Flag Component ─────────────────────────────────────────
const PaymentMethodFlag = ({ method, amount = 0 }) => {
  const razorpayEligible = amount < 50000;

  const methods = {
    "razorpay": {
      flag: "🚀 Instant",
      color: "bg-green-100 text-green-700 border-green-300",
      description: "Immediate processing",
      show: razorpayEligible
    },
    "lc": {
      flag: "🐢 Slower",
      color: "bg-blue-100 text-blue-700 border-blue-300",
      description: "Requires verification (2-3 days)",
      show: true
    },
    "bank_transfer": {
      flag: "⏱️ Medium",
      color: "bg-orange-100 text-orange-700 border-orange-300",
      description: "Requires verification (24-48 hrs)",
      show: true
    }
  };

  const config = methods[method];
  if (!config || !config.show) return null;

  return (
    <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${config.color}`}>
      {config.flag}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ProformaInvoiceModal = ({
  open,
  onClose,
  quotation,
  order,
  isSampleReq = false,
  onSuccess,
  user,
}) => {
  const [animate, setAnimate] = useState(false);
  const [step, setStep] = useState(STEPS.INVOICE);
  useLockBodyScroll(open);



  // PDF state
  const [generating, setGenerating] = useState(false);
  const [proformaUrl, setProformaUrl] = useState("");
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfFileName, setPdfFileName] = useState("");

  // Declaration
  const [declarationChecked, setDeclarationChecked] = useState(false);

  const [freshOrderData, setFreshOrderData] = useState(null);
  const [loadingInvoiceData, setLoadingInvoiceData] = useState(true);


  // OTP
  const [showOtp, setShowOtp] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);

  // Breakdown
  const [priceBreakdown, setPriceBreakdown] = useState(null);
  const [fetchingBreakdown, setFetchingBreakdown] = useState(false);

  // Payment
  const [submitting, setSubmitting] = useState(false);
  const [failReason, setFailReason] = useState("");
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null); // "razorpay" | "lc"
  const [bankTransferDetails, setBankTransferDetails] = useState(null);

  const hasGenerated = useRef(false);

  // Computed values
  const orderData = freshOrderData || {
    ...order,
    quotation,
    isSampleReq,
    isProforma: true,
  };

  // console.log("Order data for invoice:", orderData);

  const createdBy = orderData?.createdBy || {};
  const buyerName =
    [createdBy.firstName, createdBy.lastName].filter(Boolean).join(" ") || "N/A";
  const buyerAddress = buildAddress(orderData?.address || createdBy?.address);
  const buyerMobile = createdBy.phone || createdBy.phoneNumber || "N/A";

  const today = new Date();
  const dateStr = today.toLocaleDateString("en-GB");
  const orderNo = orderData?.orderNumber || "N/A";
  const variety =
    orderData?.productVariety?.varietyName || orderData?.product?.name || "N/A";
  const batchLotNo = quotation?.batch_Lot_Number || "N/A";
  const breakdownKey = isSampleReq ? "sample" : "fullOrder";

  const currentBreakdown =
    orderData?.breakdown ||
    orderData?.quotation?.priceBreakdown?.[breakdownKey]?.breakdown ||
    orderData?.priceBreakdown?.[breakdownKey]?.breakdown ||
    quotation?.priceBreakdown?.[breakdownKey]?.breakdown ||
    priceBreakdown?.[breakdownKey]?.breakdown;

  const pricePerKg = Number(
    currentBreakdown?.pricePerKg ||
    quotation?.pricing?.commissionAdjustedAmount ||
    quotation?.pricing?.amount ||
    0
  );

  const displayQty = Number(
    currentBreakdown?.quantity || 0
  );

  const itemLabel = isSampleReq
    ? "Sample Price"
    : "Base Price";

  const subtotal = Number(
    currentBreakdown?.baseAmount || 0
  );

  const gstRate = Number(
    currentBreakdown?.gstRate || 0
  );

  const gstAmt = Number(
    currentBreakdown?.gstAmount || 0
  );

  const paymentMethod =
    currentBreakdown?.paymentDetails
      ?.eligiblePaymentMethod || null;
  const transportInfo = orderData?.quotation?.transportDetails?.find(
    (t) =>
      t?.charge_is_for === (isSampleReq ? "Sample" : "Full_Order")
  );

  // console.log("Transport info for invoice:", transportInfo);

  const courierCharge = Number(
    currentBreakdown?.transportationCharges ||
    transportInfo?.transportationAmount ||
    0
  );

  const grandTotal = Number(
    currentBreakdown?.totalAmount || 0
  );

  // Above 50k → LC is mandatory (no Razorpay at all).
  // At/below 50k → buyer can pick either.
  const forceLC = grandTotal > LC_AMOUNT_THRESHOLD;
  const isLC = forceLC || selectedPaymentMethod === "lc";
  const isBankTransfer = selectedPaymentMethod === "bank_transfer";
  const isRazorpay = !forceLC && selectedPaymentMethod === "razorpay";

  // 2% Razorpay commission when Razorpay payment option is chosen
  const RAZORPAY_COMMISSION_PERCENT = 2;
  const razorpayCommission = isRazorpay
    ? Math.round((grandTotal * RAZORPAY_COMMISSION_PERCENT) / 100)
    : 0;
  const razorpayPayableAmount = isRazorpay
    ? grandTotal + razorpayCommission
    : grandTotal;

  // ── Lifecycle ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (open) {
      setTimeout(() => setAnimate(true), 10);
      if (!hasGenerated.current) {
        hasGenerated.current = true;
        autoGeneratePDF();
      }
    } else {
      setAnimate(false);
      // reset after close animation
      setTimeout(() => {
        setStep(STEPS.INVOICE);
        setDeclarationChecked(false);
        setProformaUrl("");
        setPdfBlob(null);
        setFailReason("");
        setSelectedPaymentMethod(null);
        hasGenerated.current = false;
      }, 300);
    }
  }, [open]);

  const autoGeneratePDF = async () => {
    setGenerating(true);
    try {
      const latestData = await fetchLatestInvoiceData();

      const dataForPdf = latestData || {
        ...orderData,
        breakdown: currentBreakdown,
      };

      if (!dataForPdf.breakdown && currentBreakdown) {
        dataForPdf.breakdown = currentBreakdown;
      }

      const result = await generateInvoicePDF(dataForPdf);
      if (result?.blob) {
        setPdfBlob(result.blob);
        setPdfFileName(result.fileName);
        // Upload to S3 silently
        const { url } = await s3Upload(result.blob, "invoices/proforma");
        setProformaUrl(url);
      } else {
        setProformaUrl("generation_failed");
      }
    } catch (err) {
      console.error("[ProformaInvoiceModal] PDF gen error:", err);
      setProformaUrl("generation_failed");
    } finally {
      setGenerating(false);
    }
  };

  const fetchLatestInvoiceData = async () => {
    setLoadingInvoiceData(true);
    try {
      // ── Fetch latest order ─────────────────────────
      const orderRes = await axios.get(
        `/order/get-order-by-id/${order?._id}`
      );

      // ── Fetch latest quotation ─────────────────────
      const quotationRes = await axios.get(
        `/quotation/get-quotation-by-id/${quotation?._id}`
      );

      const latestOrder = orderRes?.data?.data;
      const latestQuotation = quotationRes?.data?.data;

      if (!latestOrder || !latestQuotation) {
        throw new Error("Latest invoice data not found");
      }

      let pb = latestQuotation?.priceBreakdown || quotation?.priceBreakdown || priceBreakdown;
      if (!pb && quotation?._id) {
        try {
          const pbRes = await axios.get(`/price-breakdown/quotation/${quotation._id}`);
          if (pbRes?.data?.success) {
            pb = pbRes.data.data;
            setPriceBreakdown(pb);
          }
        } catch (e) {
          console.warn("Could not fetch price breakdown:", e);
        }
      }

      const effectiveBreakdown =
        pb?.[breakdownKey]?.breakdown ||
        latestQuotation?.priceBreakdown?.[breakdownKey]?.breakdown ||
        currentBreakdown;

      const merged = {
        ...latestOrder,
        quotation: {
          ...latestQuotation,
          priceBreakdown: pb,
        },
        priceBreakdown: pb,
        breakdown: effectiveBreakdown,
        isSampleReq,
        isProforma: true,
      };

      setFreshOrderData(merged);

      return merged;
    } catch (err) {
      console.error("[fetchLatestInvoiceData] Error:", err);
      return {
        ...orderData,
        breakdown: currentBreakdown,
      };
    } finally {
      setLoadingInvoiceData(false);
    }
  };

  const fetchBreakdown = async () => {
    if (!quotation?._id) return;
    setFetchingBreakdown(true);
    try {
      const { data } = await axios.get(`/price-breakdown/quotation/${quotation._id}`);
      if (data?.success) setPriceBreakdown(data.data);
    } catch (err) {
      console.error("Breakdown fetch error:", err);
    } finally {
      setFetchingBreakdown(false);
    }
  };

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleClose = () => {
    if (submitting) return;
    setAnimate(false);
    setTimeout(onClose, 280);
  };

  const handleDownload = () => {
    if (pdfBlob) {
      downloadBlob(pdfBlob, pdfFileName);
    } else {
      toast.error("PDF not ready yet, please wait.");
    }
  };

  const handleProceedToDeclaration = () => {
    if (!proformaUrl || proformaUrl.includes("failed")) {
      toast.error("Invoice is still being prepared, please wait.");
      return;
    }
    setStep(STEPS.DECLARATION);
  };

  const handleDeclarationContinue = async () => {

    if (!declarationChecked) {
      toast.error("Please accept the declaration to continue.");
      return;
    }

    setSendingOtp(true);

    try {
      setStep(STEPS.OTP);
      setShowOtp(true);
    } catch (err) {
      toast.error("Failed to open OTP modal");
    } finally {
      setSendingOtp(false);
    }
  };

  const handleOtpVerified = async () => {
    setShowOtp(false);
    await fetchBreakdown();
    setStep(STEPS.BREAKDOWN);
  };

  const handleLCConfirm = async () => {
    setSubmitting(true);
    setFailReason("");
    try {
      // Finalize quotation selection without payment (LC flow)
      await finalizeQuotationSelection(null); // null paymentId = LC flow
    } catch (err) {
      setFailReason("Failed to confirm LC order. Please contact support.");
      setStep(STEPS.FAILED);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBankTransferConfirm = async () => {
    setSubmitting(true);
    setFailReason("");
    try {
      // Finalize quotation selection for bank transfer flow
      await finalizeQuotationSelection("bank_transfer"); // pass bank_transfer flag
    } catch (err) {
      setFailReason("Failed to confirm bank transfer order. Please contact support.");
      setStep(STEPS.FAILED);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePay = async () => {
    if (!selectedPaymentMethod && !forceLC) {
      setSelectedPaymentMethod("razorpay");
    }
    setSubmitting(true);
    setFailReason("");

    try {
      const loaded = await loadRazorpay();
      if (!loaded) {
        setFailReason("Could not load payment gateway. Check your internet connection.");
        setStep(STEPS.FAILED);
        setSubmitting(false);
        return;
      }

      // Calculate 2% commission for Razorpay
      const finalPayableAmount = Math.round(
        grandTotal + Math.round((grandTotal * RAZORPAY_COMMISSION_PERCENT) / 100)
      );

      // Create Razorpay order
      let orderData;
      try {
        const res = await axios.post("/payment/initiate-payment", {
          amount: finalPayableAmount,
          orderId: order?._id,
          quotationId: quotation?._id,
          quotationUniqueNumber:
            quotation?.quotationUniqueNumber,

          orderType: isSampleReq
            ? "Sample"
            : "Full_Order",
        });
        if (!res.data.success) {
          setFailReason(res.data.message || "Failed to create payment order.");
          setStep(STEPS.FAILED);
          setSubmitting(false);
          return;
        }
        orderData = res.data.data;
      } catch (err) {
        setFailReason(
          err?.response?.data?.message || "Failed to initiate payment. Please try again."
        );
        setStep(STEPS.FAILED);
        setSubmitting(false);
        return;
      }

      const { orderId, razorpayKeyId, orderDetails } = orderData;
      setSubmitting(false);
      setStep(STEPS.PROCESSING);

      const options = {
        key: razorpayKeyId,
        amount: orderDetails.amount,
        currency: orderDetails.currency || "INR",
        name: "PCM Exports",
        description: `${isSampleReq ? "Sample Order" : "Full Order"} - ${orderNo}`,
        order_id: orderId,
        prefill: {
          name: buyerName,
          email: createdBy?.email || "",
          contact: buyerMobile,
        },
        theme: { color: "#EB5C0F" },
        modal: {
          ondismiss: () => {
            setStep(STEPS.BREAKDOWN);
            toast("Payment cancelled", { icon: "ℹ️" });
          },
        },
        handler: async (response) => {
          setStep(STEPS.PROCESSING);
          try {
            // Verify payment
            const verifyRes = await axios.post("/payment/verify-payment", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });

            if (!verifyRes.data.success) {
              setFailReason(verifyRes.data.message || "Payment verification failed.");
              setStep(STEPS.FAILED);
              return;
            }

            // Select quotation
            await finalizeQuotationSelection(response.razorpay_payment_id);
          } catch (err) {
            setFailReason(
              err?.response?.data?.message ||
              "Payment verification failed. Contact support with your payment ID."
            );
            setStep(STEPS.FAILED);
          }
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        const reason =
          resp.error?.description ||
          resp.error?.reason ||
          "Payment was declined by your bank or payment provider.";
        setFailReason(reason);
        setStep(STEPS.FAILED);
      });
      rzp.open();
    } catch (err) {
      setFailReason("An unexpected error occurred. Please try again.");
      setStep(STEPS.FAILED);
      setSubmitting(false);
    }
  };

  const finalizeQuotationSelection = async (paymentIdentifier) => {
    try {
      const isLCFlow = paymentIdentifier === null;
      const isBankTransferFlow = paymentIdentifier === "bank_transfer";
      const isRazorpayFlow = typeof paymentIdentifier === "string" && !isLCFlow && !isBankTransferFlow;

      const payload = {
        isSampleRequested: isSampleReq,
        invoice_details: {
          url: proformaUrl,
          type: isSampleReq ? "Sample" : "Full_Order",
        },
        paymentDetails: isLCFlow
          ? {
            method: "Letter_Of_Credit",
          }
          : isBankTransferFlow
            ? {
              method: "Direct_Bank_Transfer",
            }
            : {
              method: "Online",
              razorpay_payment_id: paymentIdentifier,
            },
      };

      const res = await axios.patch(`/quotation/select-quotation/${quotation._id}`, payload);
      if (res.data?.success) {
        setStep(STEPS.SUCCESS);
        onSuccess?.();
      } else {
        setFailReason(res.data?.message || "Failed to confirm quotation.");
        setStep(STEPS.FAILED);
      }
    } catch (err) {
      setFailReason(
        err?.response?.data?.message || "Failed to finalize order. Contact support."
      );
      setStep(STEPS.FAILED);
    }
  };

  // ── Breakdown data ─────────────────────────────────────────────────────────
  const breakdown = priceBreakdown?.[breakdownKey]?.breakdown;
  const isTransportReady = transportInfo?.transportation_charges === "Available";

  if (!open) return null;

  // ── OTP Step uses OTPModal portal separately ───────────────────────────────
  if (step === STEPS.OTP) {
    return (
      <OTPModal
        show={showOtp}
        onClose={() => {
          setShowOtp(false);
          setStep(STEPS.DECLARATION);
        }}
        onVerified={handleOtpVerified}
        identifier={user?.phone}
      />
    );
  }

  return createPortal(
    <div
      className={`fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${animate ? "opacity-100" : "opacity-0"
        }`}
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) handleClose();
      }}
    >
      <div
        className={`relative w-full md:w-[640px] bg-white shadow-2xl md:rounded-3xl rounded-t-3xl max-h-[92vh] flex flex-col transition-all duration-300 ${animate ? "scale-100 translate-y-0" : "scale-95 md:translate-y-8 translate-y-full"
          }`}
      >
        {/* ── HEADER ── */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-primaryColor/10 flex items-center justify-center">
              <TbFileInvoice className="text-primaryColor" size={18} />
            </div>
            <div>
              <h2 className="text-base font-extrabold text-gray-900">Proforma Invoice</h2>
              <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide">
                {isSampleReq ? "Sample Order" : "Full Order"} · {orderNo}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {step === STEPS.INVOICE && pdfBlob && (
              <button
                onClick={handleDownload}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-primaryColor/10 border border-primaryColor/20 text-primaryColor text-xs font-bold hover:bg-primaryColor/20 transition"
              >
                <TbDownload size={14} />
                Save PDF
              </button>
            )}
            <button
              onClick={handleClose}
              disabled={submitting}
              className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition disabled:opacity-50"
            >
              <MdClose size={16} className="text-gray-600" />
            </button>
          </div>
        </div>

        {/* ── STEP INDICATOR ── */}
        {[STEPS.INVOICE, STEPS.DECLARATION, STEPS.OTP, STEPS.BREAKDOWN].includes(step) && (
          <div className="px-5 pt-4 flex-shrink-0">
            <StepIndicator currentStep={step} />
          </div>
        )}

        {/* ── SCROLLABLE BODY ── */}
        <div className="flex-1 overflow-y-auto px-5 pb-4">

          {/* ══════ STEP: INVOICE ══════ */}
          {step === STEPS.INVOICE && (
            <div className="space-y-4 pt-2">
              {/* Brand Card */}
              <div className="rounded-2xl bg-gradient-to-br from-[#0C3E61] to-[#1a5a8a] p-4 text-white">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-[9px] font-black uppercase tracking-[0.15em] text-white/50 mb-0.5">
                      PROFORMA INVOICE
                    </p>
                    <h3 className="text-lg font-extrabold">PCM Exports</h3>
                    <p className="text-[10px] text-white/60 mt-0.5 leading-relaxed">
                      Shop No. 104, Block-A, Bharati Tower, Forestpark Ekamra Marg,<br />Bhubaneswar, Odisha(India) — 751009
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="inline-block bg-white/15 border border-white/20 rounded-lg px-2.5 py-1.5">
                      <p className="text-[9px] text-white/50 font-semibold">GSTIN</p>
                      <p className="text-xs font-bold">21BBFPM2533K1ZW</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-white/15">
                  <div>
                    <p className="text-[9px] text-white/40 font-semibold uppercase">Order No.</p>
                    <p className="text-xs font-bold">{orderNo}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[9px] text-white/40 font-semibold uppercase">Date</p>
                    <p className="text-xs font-bold">{dateStr}</p>
                  </div>
                  <div className="text-right">
                    <span
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold ${isSampleReq
                        ? "bg-amber-400/20 text-amber-300 border border-amber-400/30"
                        : "bg-emerald-400/20 text-emerald-300 border border-emerald-400/30"
                        }`}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-current" />
                      {isSampleReq ? "Sample" : "Full Order"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Basic Details */}
              <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
                <p className="text-[10px] font-black text-primaryColor uppercase tracking-widest mb-3">
                  Basic Details
                </p>

                <Divider />

                {loadingInvoiceData ? (
                  <>
                    <SkeletonRow wide />
                    <SkeletonRow />
                  </>
                ) : (
                  <>
                    <Row label="Product Variety" value={variety} />
                    <Row label="Batch / Lot No." value={batchLotNo} />
                  </>
                )}
              </div>

              {/* Buyer Info */}
              <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
                <p className="text-[10px] font-black text-primaryColor uppercase tracking-widest mb-3">
                  Buyer Information
                </p>

                <Divider />

                {loadingInvoiceData ? (
                  <>
                    <SkeletonRow />
                    <SkeletonRow wide />
                    <SkeletonRow />
                  </>
                ) : (
                  <>
                    <Row label="Buyer Name" value={buyerName} />
                    <Row label="Address" value={buyerAddress} />
                    <Row label="Mobile" value={buyerMobile} />
                  </>
                )}
              </div>

              {/* Item & Pricing */}
              <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[10px] font-black text-primaryColor uppercase tracking-widest">
                    Item Description &amp; Pricing
                  </p>
                  <span
                    className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${isSampleReq
                      ? "bg-amber-50 text-amber-700 border-amber-200"
                      : "bg-emerald-50 text-emerald-700 border-emerald-200"
                      }`}
                  >
                    {isSampleReq ? "Sample REQUIRED" : "Full Order"}
                  </span>
                </div>
                <Divider />
                <div className="bg-gray-50 rounded-xl p-3 border border-gray-100 space-y-1">
                  <Row
                    label={itemLabel}
                    value={`${displayQty} unit × ₹${fmt(pricePerKg)}`}
                  />

                  <Row
                    label="Amount"
                    value={`₹ ${fmt(subtotal)}`}
                  />
                  <div className="border-t border-dashed border-gray-200 my-2" />
                  <Row label={`GST (${gstRate}%)`} value={`₹ ${fmt(gstAmt)}`} />
                  <Row
                    label={isSampleReq ? "Courier Charge" : "Freight & Forwarding"}
                    value={`₹ ${fmt(courierCharge)}`}
                  />
                  <div className="border-t border-dashed border-gray-200 my-2" />
                  <Row label="Grand Total(Including other charges)" value={`₹ ${fmt(grandTotal)}`} accent />
                </div>
              </div>

              {/* Note */}
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
                <p className="text-xs text-amber-800 italic leading-relaxed">
                  * This is a Proforma Invoice. Our admin team will review your request and
                  once verified, you will receive a confirmed invoice.
                </p>
              </div>

              {/* PDF status */}
              {generating && (
                <div className="flex items-center justify-center gap-2 py-3 text-gray-400 text-sm">
                  <TbLoader2 size={16} className="animate-spin" />
                  Generating PDF document...
                </div>
              )}
            </div>
          )}

          {/* ══════ STEP: DECLARATION ══════ */}
          {step === STEPS.DECLARATION && (
            <div className="space-y-4 pt-2">
              <div className="flex items-center justify-center">
                <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center">
                  <TbShieldCheck size={28} className="text-blue-600" />
                </div>
              </div>
              <div className="text-center">
                <h3 className="text-lg font-extrabold text-gray-900">Buyer Declaration</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Please read and accept the declaration to proceed
                </p>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5 text-sm text-gray-700 leading-relaxed space-y-3 max-h-64 overflow-y-auto">
                <p className="font-bold text-gray-900">I hereby declare that:</p>
                <ol className="list-decimal list-inside space-y-2">
                  <li>
                    I, the buyer, have reviewed the Proforma Invoice and confirm that all
                    details including product variety, quantity, pricing, and delivery
                    address are correct.
                  </li>
                  <li>
                    I understand that this is a Proforma Invoice and the final Tax Invoice
                    will be issued upon confirmation and processing of the order.
                  </li>
                  <li>
                    I agree to the terms and conditions of PCM Exports including payment
                    terms, delivery timelines, and dispute resolution policies.
                  </li>
                  <li>
                    I confirm that the goods ordered are for legitimate commercial purposes
                    and comply with all applicable laws and regulations.
                  </li>
                  <li>
                    I understand that once the order is confirmed and payment is processed, if due to any unavoidable reasons the order gets cancelled because of the supplier, PCM Exports will not be held responsible for it. It will cancel the order and refund the whole amount.
                  </li>
                  <li>
                    All disputes are subject to Bhubaneswar jurisdiction only.
                  </li>
                </ol>
              </div>

              <label className="flex items-start gap-3 cursor-pointer group">
                <div className="mt-0.5 flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={declarationChecked}
                    onChange={(e) => setDeclarationChecked(e.target.checked)}
                    className="w-4 h-4 accent-primaryColor cursor-pointer"
                  />
                </div>
                <span className="text-sm text-gray-700 font-medium group-hover:text-gray-900 transition">
                  I have read, understood, and agree to the above declaration and the
                  Terms &amp; Conditions of PCM Exports.
                </span>
              </label>
            </div>
          )}

          {/* ══════ STEP: BREAKDOWN + PAY ══════ */}
          {step === STEPS.BREAKDOWN && (
            <div className="space-y-4 pt-2">
              <div className="text-center mb-2">
                <h3 className="text-lg font-extrabold text-gray-900">Order Summary</h3>
                <p className="text-sm text-gray-500">Review your pricing before payment</p>
              </div>

              {fetchingBreakdown ? (
                <div className="flex flex-col items-center justify-center py-10 gap-3 text-gray-400">
                  <TbLoader2 size={28} className="animate-spin text-primaryColor" />
                  <p className="text-sm">Loading price breakdown...</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Base Price */}
                  <div className="flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl border bg-gray-50 border-gray-100">
                    <div className="flex items-center gap-2.5">
                      <span className="text-gray-400">
                        <FiPackage size={14} />
                      </span>

                      <div>
                        <p className="text-sm font-semibold text-gray-800">
                          {isSampleReq ? "Sample Price" : "Base Price"}
                        </p>

                        <p className="text-xs text-gray-400">
                          {breakdown
                            ? `${breakdown.quantity || 0} unit × ₹${fmt(
                              breakdown.pricePerKg
                            )}`
                            : `${displayQty} unit × ₹${fmt(pricePerKg)}`}
                        </p>
                      </div>
                    </div>

                    <p className="text-sm font-bold text-gray-900 flex-shrink-0">
                      {breakdown
                        ? `₹${fmt(breakdown.baseAmount)}`
                        : `₹${fmt(subtotal)}`}
                    </p>
                  </div>

                  {/* GST */}
                  <div className="flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl border bg-gray-50 border-gray-100">
                    <div className="flex items-center gap-2.5">
                      <span className="text-gray-400">
                        <TbFileInvoice size={14} />
                      </span>

                      <div>
                        <p className="text-sm font-semibold text-gray-800">
                          GST
                        </p>

                        <p className="text-xs text-gray-400">
                          {breakdown?.gstRate || gstRate}% applied
                        </p>
                      </div>
                    </div>

                    <p className="text-sm font-bold text-gray-900 flex-shrink-0">
                      {breakdown
                        ? `₹${fmt(breakdown.gstAmount)}`
                        : `₹${fmt(gstAmt)}`}
                    </p>
                  </div>

                  {/* Transport */}
                  <div className="flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl border bg-gray-50 border-gray-100">
                    <div className="flex items-center gap-2.5">
                      <FiTruck className="text-primaryColor flex-shrink-0" size={14} />
                      <div>
                        <p className="text-sm font-semibold text-gray-800">
                          {isSampleReq ? "Courier Charge" : "Freight & Forwarding"}
                        </p>
                        <p className="text-xs text-gray-400">
                          {breakdown && isTransportReady
                            ? "Admin verified charges"
                            : "Standard charges applied"}
                        </p>
                      </div>
                    </div>
                    <p className="text-sm font-bold text-gray-900 flex-shrink-0">
                      ₹{breakdown && isTransportReady
                        ? fmt(breakdown.transportationCharges)
                        : fmt(courierCharge)}
                    </p>
                  </div>

                  {/* Razorpay 2% Commission line item */}
                  {isRazorpay && (
                    <div className="flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl border bg-amber-50/80 border-amber-200 animate-fadeIn">
                      <div className="flex items-center gap-2.5">
                        <span className="text-amber-600">
                          <TbCreditCard size={15} />
                        </span>
                        <div>
                          <p className="text-sm font-semibold text-amber-900">
                            Razorpay Gateway Commission (2%)
                          </p>
                          <p className="text-xs text-amber-600">
                            2% online payment gateway processing charge
                          </p>
                        </div>
                      </div>
                      <p className="text-sm font-bold text-amber-900 flex-shrink-0">
                        + ₹{fmt(razorpayCommission)}
                      </p>
                    </div>
                  )}

                  {/* Grand Total */}
                  <div className="relative rounded-2xl overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-primaryColor to-[#c44a0b]" />
                    <div className="relative z-10 flex items-center justify-between gap-3 px-5 py-4">
                      <div>
                        <p className="text-xs text-white/70 font-semibold">
                          {isRazorpay
                            ? "Total Payable (Incl. 2% Razorpay Commission)"
                            : "Grand Total(Including other charges)"}
                        </p>
                        <p className="text-[11px] text-white/40 mt-0.5">
                          {isRazorpay
                            ? `Order ₹${fmt(grandTotal)} + ₹${fmt(razorpayCommission)} gateway charge`
                            : "Including all taxes and charges"}
                        </p>
                      </div>
                      <p className="text-2xl font-extrabold text-white">
                        ₹{fmt(isRazorpay ? razorpayPayableAmount : grandTotal)}
                      </p>
                    </div>
                  </div>

                  {forceLC ? (
                    <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-blue-100 bg-blue-50">
                      <div>
                        <p className="text-sm font-semibold text-blue-800">Payment Method</p>
                        <p className="text-xs text-blue-500 mt-1">
                          Orders above ₹{fmt(LC_AMOUNT_THRESHOLD)} require Letter of Credit
                        </p>
                      </div>
                      <div className="px-3 py-1 rounded-lg text-xs font-bold bg-blue-100 text-blue-700 border border-blue-200">
                        LC ONLY
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs font-semibold text-gray-600 px-1 mb-2">Select Payment Method</p>
                        <div className="grid grid-cols-1 gap-2.5">

                          {/* RAZORPAY OPTION */}
                          {grandTotal < LC_AMOUNT_THRESHOLD && (
                            <button
                              type="button"
                              onClick={() => setSelectedPaymentMethod("razorpay")}
                              className={`flex items-start gap-3 p-3 rounded-xl border-2 transition ${selectedPaymentMethod === "razorpay"
                                ? "border-primaryColor bg-primaryColor/5"
                                : "border-gray-100 bg-white hover:border-gray-200"
                                }`}
                            >
                              <div className="flex-shrink-0 mt-0.5">
                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedPaymentMethod === "razorpay"
                                  ? "border-primaryColor bg-primaryColor"
                                  : "border-gray-300"
                                  }`}>
                                  {selectedPaymentMethod === "razorpay" && (
                                    <div className="w-1.5 h-1.5 rounded-full bg-white" />
                                  )}
                                </div>
                              </div>
                              <div className="flex-1 text-left">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-sm font-bold text-gray-900">Razorpay Payment</span>
                                  <PaymentMethodFlag method="razorpay" amount={grandTotal} />
                                  <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                                    +2% Commission
                                  </span>
                                </div>
                                <p className="text-xs text-gray-600">Pay via credit/debit card, UPI, or net banking. Instant confirmation.</p>
                              </div>
                              <div className="text-lg">💳</div>
                            </button>
                          )}

                          {/* LETTER OF CREDIT OPTION */}
                          <button
                            type="button"
                            onClick={() => setSelectedPaymentMethod("lc")}
                            className={`flex items-start gap-3 p-3 rounded-xl border-2 transition ${selectedPaymentMethod === "lc"
                              ? "border-blue-500 bg-blue-50"
                              : "border-gray-100 bg-white hover:border-gray-200"
                              }`}
                          >
                            <div className="flex-shrink-0 mt-0.5">
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedPaymentMethod === "lc"
                                ? "border-blue-600 bg-blue-600"
                                : "border-gray-300"
                                }`}>
                                {selectedPaymentMethod === "lc" && (
                                  <div className="w-1.5 h-1.5 rounded-full bg-white" />
                                )}
                              </div>
                            </div>
                            <div className="flex-1 text-left">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-sm font-bold text-gray-900">Letter of Credit (LC)</span>
                                <PaymentMethodFlag method="lc" amount={grandTotal} />
                              </div>
                              <p className="text-xs text-gray-600">Submit LC document for verification. Best for B2B transactions.</p>
                            </div>
                            <div className="text-lg">💼</div>
                          </button>

                          {/* DIRECT BANK TRANSFER OPTION */}
                          <button
                            type="button"
                            onClick={() => setSelectedPaymentMethod("bank_transfer")}
                            className={`flex items-start gap-3 p-3 rounded-xl border-2 transition ${selectedPaymentMethod === "bank_transfer"
                              ? "border-orange-500 bg-orange-50"
                              : "border-gray-100 bg-white hover:border-gray-200"
                              }`}
                          >
                            <div className="flex-shrink-0 mt-0.5">
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedPaymentMethod === "bank_transfer"
                                ? "border-orange-600 bg-orange-600"
                                : "border-gray-300"
                                }`}>
                                {selectedPaymentMethod === "bank_transfer" && (
                                  <div className="w-1.5 h-1.5 rounded-full bg-white" />
                                )}
                              </div>
                            </div>
                            <div className="flex-1 text-left">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-sm font-bold text-gray-900">Direct Bank Transfer</span>
                                <PaymentMethodFlag method="bank_transfer" amount={grandTotal} />
                              </div>
                              <p className="text-xs text-gray-600">Transfer to our bank account. Upload receipt for verification.</p>
                            </div>
                            <div className="text-lg">🏦</div>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Payment method info / Notice */}
                  {selectedPaymentMethod === "lc" ? (
                    <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 space-y-2">
                      <div className="flex items-center gap-2 mb-1">
                        <TbShieldCheck size={16} className="text-blue-600" />
                        <p className="text-sm font-bold text-blue-700">
                          Letter of Credit (LC) Process
                        </p>
                      </div>

                      <p className="text-xs text-blue-600 leading-relaxed">
                        This order will proceed through a Letter of Credit (LC). Your LC must be
                        verified by PCM Exports before the supplier is allowed to dispatch the order.
                      </p>

                      <ul className="text-xs text-blue-600 space-y-1 list-disc list-inside">
                        <li>
                          After clicking <strong>"Confirm via Letter of Credit"</strong>, you will be
                          taken to the LC submission page.
                        </li>

                        <li>
                          Upload your Letter of Credit along with all supporting documents available.
                          Providing complete documents helps speed up verification.
                        </li>

                        <li>
                          Our admin team will review and verify the submitted LC.
                        </li>

                        <li>
                          If the LC is approved, your order will move forward for dispatch.
                        </li>

                        <li>
                          If the LC is rejected, you will be notified and asked to upload a corrected
                          LC again.
                        </li>

                        <li>
                          The supplier cannot dispatch the order until the LC has been successfully
                          verified.
                        </li>
                      </ul>

                      <p className="text-xs font-semibold text-blue-700 bg-blue-100 rounded-xl px-3 py-2 mt-2">
                        💼 Complete LC verification is mandatory before dispatch can begin.
                      </p>
                    </div>
                  ) : selectedPaymentMethod === "bank_transfer" ? (
                    <div className="bg-orange-50 border border-orange-200 rounded-2xl p-4 space-y-2">
                      <div className="flex items-center gap-2 mb-1">
                        <TbCurrencyRupee size={16} className="text-orange-600" />
                        <p className="text-sm font-bold text-orange-700">
                          Direct Bank Transfer Process
                        </p>
                      </div>

                      <p className="text-xs text-orange-600 leading-relaxed">
                        Complete the payment directly to the official PCM Exports bank account.
                        Your payment must be verified before order processing continues.
                      </p>

                      <ul className="text-xs text-orange-600 space-y-1 list-disc list-inside">
                        <li>
                          After clicking <strong>"Confirm & Proceed"</strong>, you will be taken to
                          the bank transfer page.
                        </li>

                        <li>
                          The official PCM Exports bank account details will be displayed.
                        </li>

                        <li>
                          Complete the bank transfer using your preferred banking method.
                        </li>

                        <li>
                          Upload the payment screenshot or transaction receipt after completing the
                          transfer.
                        </li>

                        <li>
                          Provide the required transaction details such as UTR/Reference Number,
                          payment date, and other requested information.
                        </li>

                        <li>
                          Our admin team will verify the payment before processing your order further.
                        </li>
                      </ul>

                      <p className="text-xs font-semibold text-orange-700 bg-orange-100 rounded-xl px-3 py-2 mt-2">
                        🏦 Payment verification is required before dispatch can begin.
                      </p>
                    </div>
                  ) : selectedPaymentMethod === "razorpay" ? (
                    <div className="bg-green-50 border border-green-200 rounded-2xl p-3.5 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5 text-green-800 font-semibold text-xs">
                          <TbShieldCheck size={16} className="text-green-600" />
                          <span>Secured by Razorpay · Cards · UPI · Netbanking</span>
                        </div>
                        <span className="text-[10px] font-bold text-amber-700 bg-amber-100 border border-amber-200 px-2 py-0.5 rounded-full">
                          +2% Commission
                        </span>
                      </div>
                      <div className="pt-2 border-t border-green-200/60 flex items-center justify-between text-xs text-gray-700">
                        <span>Order Amount: <strong>₹{fmt(grandTotal)}</strong></span>
                        <span>Gateway Charge (2%): <strong className="text-amber-700">+₹{fmt(razorpayCommission)}</strong></span>
                        <span>Net Payable: <strong className="text-primaryColor text-sm font-black">₹{fmt(razorpayPayableAmount)}</strong></span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs text-gray-400 text-center py-2">
                      Select a payment method above to see details
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ══════ STEP: PROCESSING ══════ */}
          {step === STEPS.PROCESSING && (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <TbLoader2 size={44} className="text-primaryColor animate-spin" />
              <h3 className="text-lg font-extrabold text-gray-900">Processing Payment</h3>
              <p className="text-sm text-gray-500 text-center max-w-xs">
                Please complete the payment in the Razorpay window. Do not close or
                refresh this page.
              </p>
            </div>
          )}

          {/* ══════ STEP: SUCCESS ══════ */}
          {step === STEPS.SUCCESS && (
            <div className="flex flex-col items-center justify-center py-12 gap-5">
              <div
                className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center"
                style={{ animation: "popIn 0.4s cubic-bezier(0.34,1.56,0.64,1) both" }}
              >
                <MdOutlineCheckCircle size={44} className="text-green-600" />
              </div>
              <div className="text-center">
                <h3 className="text-xl font-extrabold text-gray-900">
                  {isSampleReq ? "Sample Order Confirmed! 🎉" : "Order Placed! 🎉"}
                </h3>
                <p className="text-sm text-gray-500 mt-2 max-w-xs">
                  {isSampleReq
                    ? "Your sample order has been confirmed. PCM Exports will dispatch the sample shortly."
                    : selectedPaymentMethod === "lc"
                      ? "Your full order has been reserved via Letter of Credit. Our team will contact you within 24 hours to complete the LC process."
                      : selectedPaymentMethod === "bank_transfer"
                        ? "Your full order has been reserved. Please proceed to upload bank transfer proof in the next step."
                        : "Your full order has been successfully confirmed. PCM Exports will process it shortly."}
                </p>
              </div>
              <div className={`w-full rounded-2xl p-4 text-center ${selectedPaymentMethod === "lc"
                ? "bg-blue-50 border border-blue-200"
                : selectedPaymentMethod === "bank_transfer"
                  ? "bg-orange-50 border border-orange-200"
                  : "bg-green-50 border border-green-200"
                }`}>
                <p className={`text-xs font-semibold ${selectedPaymentMethod === "lc"
                  ? "text-blue-700"
                  : selectedPaymentMethod === "bank_transfer"
                    ? "text-orange-700"
                    : "text-green-700"
                  }`}>
                  {selectedPaymentMethod === "lc"
                    ? "Order reserved. Our team will contact you for LC document exchange."
                    : selectedPaymentMethod === "bank_transfer"
                      ? "Next: Upload bank transfer receipt in the order details page."
                      : "Proforma Invoice has been saved to your order."}
                </p>
              </div>
            </div>
          )}

          {/* ══════ STEP: FAILED ══════ */}
          {step === STEPS.FAILED && (
            <div className="flex flex-col items-center justify-center py-10 gap-4">
              <div
                className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center"
                style={{ animation: "popIn 0.35s cubic-bezier(0.34,1.56,0.64,1) both" }}
              >
                <TbAlertCircle size={32} className="text-red-500" />
              </div>
              <div className="text-center">
                <h3 className="text-lg font-extrabold text-gray-900">Payment Failed</h3>
                <p className="text-sm text-gray-500 mt-1">
                  {failReason || "Something went wrong. Your order was not placed."}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* ── FOOTER ACTIONS ── */}
        <div className="px-5 py-4 border-t border-gray-100 flex-shrink-0">
          {step === STEPS.INVOICE && (
            <button
              onClick={handleProceedToDeclaration}
              disabled={generating || !proformaUrl || proformaUrl.includes("failed")}
              // disabled={generating}
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-primaryColor text-white font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-all duration-200 shadow-lg shadow-primaryColor/30"
            >
              {generating ? (
                <>
                  <TbLoader2 size={16} className="animate-spin" />
                  Preparing Document...
                </>
              ) : (
                <>
                  <TbArrowRight size={16} />
                  Confirm & Continue
                </>
              )}
            </button>
          )}

          {step === STEPS.DECLARATION && (
            <div className="flex gap-3">
              <button
                onClick={() => setStep(STEPS.INVOICE)}
                className="flex-1 py-3.5 border-2 border-gray-200 text-gray-600 rounded-xl font-semibold text-sm hover:bg-gray-50 transition"
              >
                Back
              </button>
              <button
                onClick={handleDeclarationContinue}
                disabled={!declarationChecked || sendingOtp}
                className="flex-[2] flex items-center justify-center gap-2 py-3.5 bg-primaryColor text-white rounded-xl font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition shadow-lg shadow-primaryColor/30"
              >
                {sendingOtp ? (
                  <>
                    <TbLoader2 size={16} className="animate-spin" />
                    Sending OTP...
                  </>
                ) : (
                  <>
                    <TbShieldCheck size={16} />
                    I Agree — Send OTP
                  </>
                )}
              </button>
            </div>
          )}

          {step === STEPS.BREAKDOWN && (
            selectedPaymentMethod === "lc" ? (
              <button
                onClick={handleLCConfirm}
                disabled={submitting || fetchingBreakdown}
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-blue-600 text-white font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-all shadow-lg shadow-blue-600/30"
              >
                {submitting ? (
                  <>
                    <TbLoader2 size={16} className="animate-spin" />
                    Confirming...
                  </>
                ) : (
                  <>
                    <TbShieldCheck size={16} />
                    Confirm via Letter of Credit
                  </>
                )}
              </button>
            ) : selectedPaymentMethod === "bank_transfer" ? (
              <button
                onClick={handleBankTransferConfirm}
                disabled={submitting || fetchingBreakdown}
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-orange-600 text-white font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-all shadow-lg shadow-orange-600/30"
              >
                {submitting ? (
                  <>
                    <TbLoader2 size={16} className="animate-spin" />
                    Confirming...
                  </>
                ) : (
                  <>
                    <TbCurrencyRupee size={16} />
                    Confirm & Proceed to Bank Transfer
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={handlePay}
                disabled={submitting || fetchingBreakdown}
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-gradient-to-r from-primaryColor to-[#c44a0b] text-white font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-all shadow-lg shadow-primaryColor/30"
              >
                {submitting ? (
                  <>
                    <TbLoader2 size={16} className="animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <TbCurrencyRupee size={16} />
                    {selectedPaymentMethod === "razorpay"
                      ? `Pay ₹${fmt(razorpayPayableAmount)} via Razorpay`
                      : selectedPaymentMethod === null
                        ? `Select Payment Method`
                        : `Pay ₹${fmt(razorpayPayableAmount)} via Razorpay`}
                  </>
                )}
              </button>
            )
          )}

          {step === STEPS.SUCCESS && (
            <button
              onClick={handleClose}
              className="w-full py-3.5 bg-primaryColor text-white rounded-xl font-bold text-sm hover:opacity-90 transition shadow-lg shadow-primaryColor/30"
            >
              Done
            </button>
          )}

          {step === STEPS.FAILED && (
            <div className="flex gap-3">
              <button
                onClick={handleClose}
                className="flex-1 py-3.5 border-2 border-gray-200 text-gray-600 rounded-xl font-semibold text-sm hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setFailReason("");
                  setStep(STEPS.BREAKDOWN);
                }}
                className="flex-[2] flex items-center justify-center gap-2 py-3.5 bg-primaryColor text-white rounded-xl font-bold text-sm hover:opacity-90 transition shadow-lg shadow-primaryColor/30"
              >
                <TbRefresh size={16} />
                Try Again
              </button>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes popIn {
          from { transform: scale(0.4); opacity: 0; }
          to   { transform: scale(1);   opacity: 1; }
        }
      `}</style>
    </div>,
    document.body
  );
};

export default ProformaInvoiceModal;